import React, { useEffect, useState, useRef } from 'react'
import { Field } from 'msp-components'
import { useIntl } from 'react-intl'
import { useConfig, useCommerceAPI } from 'msp-integrations'
import getAddressFields from './AddressFields.fields'
import { AddressFieldsProps } from './AddressFields.types'
import {
  useSuggestions,
  useClickOutsideAndEscape
} from './hooks/useSuggestions'
import { SuggestionDropdown } from './components/SuggestionDropdown'
import { createKeyboardNavigationHandler } from './utils/keyboardNavigation'

type AddressSuggestion = {
  uuid: string
  postalCode: string
  city: string
  district: string
  street: string
  houseNumber: string
  distributionCode: string
}

type PostalCodeCitySuggestion = {
  uuid: string
  postalCode: string
  city: string
}

const AddressFields = ({ className, form }: AddressFieldsProps) => {
  const fieldConfig = getAddressFields()
  const {
    theme: { font }
  } = useConfig()
  const { formatMessage } = useIntl()
  const api = useCommerceAPI() as any

  // Suggestion states using custom hook
  const [addressState, addressActions] = useSuggestions<AddressSuggestion>()
  const [postalCodeState, postalCodeActions] =
    useSuggestions<PostalCodeCitySuggestion>()
  const [cityState, cityActions] = useSuggestions<PostalCodeCitySuggestion>()

  // House number validation state
  const [showHouseNumberWarning, setShowHouseNumberWarning] = useState(false)

  // Track if this is the first render
  const isInitialMount = useRef(true)

  if (!form) return null

  const address1Value = form.watch('address1')
  const postalCodeValue = form.watch('postalCode')
  const cityValue = form.watch('city')
  const countryCodeValue = form.watch('countryCode')

  // Check if address contains a number (house number)
  const hasHouseNumber = (address: string) => {
    return /\d/.test(address)
  }

  // Reset form when country changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (countryCodeValue) {
      // Reset address fields
      form.setValue('address1', '')
      form.setValue('address2', '')
      form.setValue('postalCode', '')
      form.setValue('city', '')

      // Reset all suggestion states
      addressActions.reset()
      postalCodeActions.reset()
      cityActions.reset()

      // Reset house number warning
      setShowHouseNumberWarning(false)
    }
  }, [countryCodeValue])

  // Check for house number when address changes
  useEffect(() => {
    if (addressState.debounceTimer.current) {
      clearTimeout(addressState.debounceTimer.current)
    }

    if (
      address1Value &&
      address1Value.length > 0 &&
      !addressState.isUserTyping
    ) {
      setShowHouseNumberWarning(!hasHouseNumber(address1Value))
    } else {
      setShowHouseNumberWarning(false)
    }
  }, [address1Value, addressState.isUserTyping])

  // Handle click outside and escape key
  useClickOutsideAndEscape(
    [
      addressState.suggestionsRef,
      postalCodeState.suggestionsRef,
      cityState.suggestionsRef
    ],
    [addressActions.hideAll, postalCodeActions.hideAll, cityActions.hideAll],
    [
      addressState.showSuggestions,
      postalCodeState.showSuggestions,
      cityState.showSuggestions
    ]
  )

  // Fetch address suggestions
  useEffect(() => {
    if (addressState.debounceTimer.current) {
      clearTimeout(addressState.debounceTimer.current)
    }

    if (
      address1Value &&
      address1Value.length >= 2 &&
      addressState.isUserTyping
    ) {
      addressState.debounceTimer.current = setTimeout(async () => {
        addressActions.setIsLoading(true)
        try {
          const combinedValue = [postalCodeValue, cityValue, address1Value]
            .join(' ')
            .trim()

          const params: any = {
            parameters: {
              c_combined: combinedValue,
              c_countryCode: countryCodeValue || 'DE' // Default to DE if not provided
            }
          }

          const response = await api.postdirektApi.getBuildings(params)

          if (response?.success && response?.buildings) {
            const formattedSuggestions = response.buildings.map(
              (item: any) => ({
                uuid: item.uuid,
                postalCode: item.postalCode,
                city: item.city,
                district: item.district,
                street: item.street,
                houseNumber: item.houseNumber,
                distributionCode: item.distributionCode
              })
            )
            addressActions.setSuggestions(formattedSuggestions)
            addressActions.setShowSuggestions(formattedSuggestions.length > 0)
            addressActions.setIsExpanded(false)
          } else {
            addressActions.reset()
          }
        } catch (error) {
          console.error('Error fetching address suggestions:', error)
          addressActions.reset()
        } finally {
          addressActions.setIsLoading(false)
        }
      }, 500)
    } else if (!address1Value || address1Value.length < 2) {
      // Only reset if the value is too short or empty, not just because user stopped typing
      addressActions.reset()
    }

    return () => {
      if (addressState.debounceTimer.current) {
        clearTimeout(addressState.debounceTimer.current)
      }
    }
  }, [address1Value, addressState.isUserTyping, api])

  // Fetch postal code suggestions
  useEffect(() => {
    if (postalCodeState.debounceTimer.current) {
      clearTimeout(postalCodeState.debounceTimer.current)
    }

    if (
      postalCodeValue &&
      postalCodeValue.length >= 2 &&
      postalCodeState.isUserTyping
    ) {
      postalCodeState.debounceTimer.current = setTimeout(async () => {
        postalCodeActions.setIsLoading(true)
        try {
          const params: any = {
            parameters: {
              c_postalCode: postalCodeValue,
              c_city: postalCodeValue,
              c_countryCode: countryCodeValue || 'DE' // Default to DE if not provided
            }
          }

          const response = await api.postdirektApi.getPostalcodeCities(params)

          if (response?.success && response?.addresses) {
            const formattedSuggestions = response.addresses.map(
              (item: any) => ({
                uuid: item.uuid,
                postalCode: item.postalCode,
                city: item.city
              })
            )
            postalCodeActions.setSuggestions(formattedSuggestions)
            postalCodeActions.setShowSuggestions(
              formattedSuggestions.length > 0
            )
            postalCodeActions.setIsExpanded(false)
          } else {
            postalCodeActions.reset()
          }
        } catch (error) {
          console.error('Error fetching postal code suggestions:', error)
          postalCodeActions.reset()
        } finally {
          postalCodeActions.setIsLoading(false)
        }
      }, 500)
    } else if (!postalCodeValue || postalCodeValue.length < 2) {
      // Only reset if the value is too short or empty, not just because user stopped typing
      postalCodeActions.reset()
    }

    return () => {
      if (postalCodeState.debounceTimer.current) {
        clearTimeout(postalCodeState.debounceTimer.current)
      }
    }
  }, [postalCodeValue, postalCodeState.isUserTyping, api])

  // Fetch city suggestions
  useEffect(() => {
    if (cityState.debounceTimer.current) {
      clearTimeout(cityState.debounceTimer.current)
    }

    if (
      (cityValue && cityValue.length >= 2 && cityState.isUserTyping) ||
      (postalCodeValue && postalCodeValue.length >= 2 && cityState.isUserTyping)
    ) {
      cityState.debounceTimer.current = setTimeout(async () => {
        cityActions.setIsLoading(true)
        try {
          const params: any = {
            parameters: {
              c_postalCode: postalCodeValue,
              c_countryCode: countryCodeValue || 'DE', // Default to DE if not provided
              c_city: cityValue || postalCodeValue
            }
          }

          const response = await api.postdirektApi.getPostalcodeCities(params)

          if (response?.success && response?.addresses) {
            const formattedSuggestions = response.addresses.map(
              (item: any) => ({
                uuid: item.uuid,
                city: item.city
              })
            )
            cityActions.setSuggestions(formattedSuggestions)
            cityActions.setShowSuggestions(formattedSuggestions.length > 0)
            cityActions.setIsExpanded(false)
          } else {
            cityActions.reset()
          }
        } catch (error) {
          console.error('Error fetching city suggestions:', error)
          cityActions.reset()
        } finally {
          cityActions.setIsLoading(false)
        }
      }, 500)
    } else if (
      (!cityValue || cityValue.length < 2) &&
      (!postalCodeValue || postalCodeValue.length < 2)
    ) {
      // Only reset if the value is too short or empty, not just because user stopped typing
      cityActions.reset()
    }

    return () => {
      if (cityState.debounceTimer.current) {
        clearTimeout(cityState.debounceTimer.current)
      }
    }
  }, [cityValue, cityState.isUserTyping, api])

  // Click handlers
  const handleAddressSuggestionClick = (suggestion: AddressSuggestion) => {
    const fullAddress = `${suggestion.street} ${suggestion.houseNumber}`
    form.setValue('address1', fullAddress, { shouldValidate: true })
    form.setValue('city', suggestion.city, { shouldValidate: true })
    form.setValue('postalCode', suggestion.postalCode, { shouldValidate: true })
    addressActions.setShowSuggestions(false)
    addressActions.setSelectedIndex(-1)
    // Set isUserTyping to false after values are set
    setTimeout(() => {
      addressActions.setIsUserTyping(false)
    }, 0)
  }

  const handlePostalCodeSuggestionClick = (
    suggestion: PostalCodeCitySuggestion
  ) => {
    postalCodeActions.setIsUserTyping(false)
    form.setValue('postalCode', suggestion.postalCode, { shouldValidate: true })
    form.setValue('city', suggestion.city, { shouldValidate: true })
    postalCodeActions.reset()
    postalCodeActions.setSelectedIndex(-1)
  }

  const handleCitySuggestionClick = (suggestion: PostalCodeCitySuggestion) => {
    cityActions.setIsUserTyping(false)
    form.setValue('city', suggestion.city, { shouldValidate: true })
    cityActions.reset()
    cityActions.setSelectedIndex(-1)
  }

  // Toggle expand handlers
  const toggleAddressExpanded = (e: React.MouseEvent) => {
    e.stopPropagation()
    addressActions.setIsExpanded(!addressState.isExpanded)
  }

  const togglePostalCodeExpanded = (e: React.MouseEvent) => {
    e.stopPropagation()
    postalCodeActions.setIsExpanded(!postalCodeState.isExpanded)
  }

  const toggleCityExpanded = (e: React.MouseEvent) => {
    e.stopPropagation()
    cityActions.setIsExpanded(!cityState.isExpanded)
  }

  // Keyboard navigation handlers
  const handleAddressKeyDown = createKeyboardNavigationHandler(
    addressState.showSuggestions,
    addressState.suggestions,
    addressState.isExpanded,
    addressState.selectedIndex,
    addressActions.setSelectedIndex,
    addressActions.setIsExpanded,
    handleAddressSuggestionClick
  )

  const handlePostalCodeKeyDown = createKeyboardNavigationHandler(
    postalCodeState.showSuggestions,
    postalCodeState.suggestions,
    postalCodeState.isExpanded,
    postalCodeState.selectedIndex,
    postalCodeActions.setSelectedIndex,
    postalCodeActions.setIsExpanded,
    handlePostalCodeSuggestionClick
  )

  const handleCityKeyDown = createKeyboardNavigationHandler(
    cityState.showSuggestions,
    cityState.suggestions,
    cityState.isExpanded,
    cityState.selectedIndex,
    cityActions.setSelectedIndex,
    cityActions.setIsExpanded,
    handleCitySuggestionClick
  )

  return (
    <div className={`flex flex-col ${className}`}>
      <div className='flex flex-row'>
        <Field
          control={form.control}
          config={fieldConfig.salutation}
          error={form.formState.errors?.salutation}
          isDisabled={form.formState.isSubmitting}
        />
      </div>
      <div className={'flex flex-col md:flex-row md:space-x-3'}>
        <Field
          control={form.control}
          config={fieldConfig.firstName}
          error={form.formState.errors?.firstName}
          isDisabled={form.formState.isSubmitting}
        />
        <Field
          control={form.control}
          config={fieldConfig.lastName}
          error={form.formState.errors?.lastName}
          isDisabled={form.formState.isSubmitting}
        />
      </div>
      <Field
        control={form.control}
        config={fieldConfig.companyName}
        error={form.formState.errors?.companyName}
        isDisabled={form.formState.isSubmitting}
      />
      <Field
        control={form.control}
        config={{
          ...fieldConfig.phone,
          onUpdatePrefix: (value: any) => form.setValue('phonePrefix', value),
          initialPrefix: form.getValues('phonePrefix')
        }}
        error={form.formState.errors?.phone}
        isDisabled={form.formState.isSubmitting}
      />
      <Field
        control={form.control}
        config={fieldConfig.countryCode}
        error={form.formState.errors?.countryCode}
        onInit={(values: any) =>
          (values || []).forEach(({ id, value }: any) =>
            form.setValue(id, value)
          )
        }
        isDisabled={form.formState.isSubmitting}
      />
      <div className={'grid grid-cols-1 md:grid-cols-3 md:gap-3'}>
        <div className='relative' onKeyDown={handlePostalCodeKeyDown}>
          <Field
            control={form.control}
            config={{
              ...fieldConfig.postalCode,
              onChange: (value: any) => {
                postalCodeActions.setIsUserTyping(true)
                postalCodeActions.setSelectedIndex(-1)
                if (fieldConfig.postalCode.onChange) {
                  fieldConfig.postalCode.onChange(value)
                }
              },
              onBlur: () => {
                setTimeout(() => {
                  postalCodeActions.setIsUserTyping(false)
                }, 200)
              }
            }}
            error={form.formState.errors?.postalCode}
            isDisabled={form.formState.isSubmitting}
          />
          <SuggestionDropdown
            suggestions={postalCodeState.suggestions}
            showSuggestions={postalCodeState.showSuggestions}
            isLoading={postalCodeState.isLoading}
            isExpanded={postalCodeState.isExpanded}
            selectedIndex={postalCodeState.selectedIndex}
            suggestionsRef={postalCodeState.suggestionsRef}
            onSuggestionClick={handlePostalCodeSuggestionClick}
            onToggleExpanded={togglePostalCodeExpanded}
            renderSuggestion={(suggestion) =>
              `${suggestion.postalCode} - ${suggestion.city}`
            }
            fontClass={font.medium}
            formatMessage={formatMessage}
            ariaLabel='Postal code suggestions'
          />
        </div>
        <div className={'relative col-span-2'} onKeyDown={handleCityKeyDown}>
          <Field
            control={form.control}
            config={{
              ...fieldConfig.city,
              onChange: (value: any) => {
                cityActions.setIsUserTyping(true)
                cityActions.setSelectedIndex(-1)
                if (fieldConfig.city.onChange) {
                  fieldConfig.city.onChange(value)
                }
              },
              onBlur: () => {
                setTimeout(() => {
                  cityActions.setIsUserTyping(false)
                }, 200)
              }
            }}
            error={form.formState.errors?.city}
            isDisabled={form.formState.isSubmitting}
          />
          <SuggestionDropdown
            suggestions={cityState.suggestions}
            showSuggestions={cityState.showSuggestions}
            isLoading={cityState.isLoading}
            isExpanded={cityState.isExpanded}
            selectedIndex={cityState.selectedIndex}
            suggestionsRef={cityState.suggestionsRef}
            onSuggestionClick={handleCitySuggestionClick}
            onToggleExpanded={toggleCityExpanded}
            renderSuggestion={(suggestion) => suggestion.city}
            fontClass={font.medium}
            formatMessage={formatMessage}
            ariaLabel='City suggestions'
          />
        </div>
      </div>
      <div className='relative' onKeyDown={handleAddressKeyDown}>
        <Field
          control={form.control}
          config={{
            ...fieldConfig.address1,
            onChange: (value: any) => {
              addressActions.setIsUserTyping(true)
              addressActions.setSelectedIndex(-1)
              if (fieldConfig.address1.onChange) {
                fieldConfig.address1.onChange(value)
              }
            },
            onBlur: () => {
              // Delay to allow suggestion clicks to complete
              setTimeout(() => {
                addressActions.setIsUserTyping(false)
              }, 200)
            }
          }}
          error={form.formState.errors?.address1}
          isDisabled={form.formState.isSubmitting}
        />
        {showHouseNumberWarning && (
          <div
            className={`${font.regular} border-accentYellow text-accentYellow group-hover:text-accentYellow focus:border-accentYellow placeholder-accentYellow block text-left font-normal`}
          >
            {formatMessage({
              id: 'msp.address.house_number_warning',
              defaultMessage: 'Did you miss entering your house number?'
            })}
          </div>
        )}
        <SuggestionDropdown
          suggestions={addressState.suggestions}
          showSuggestions={addressState.showSuggestions}
          isLoading={addressState.isLoading}
          isExpanded={addressState.isExpanded}
          selectedIndex={addressState.selectedIndex}
          suggestionsRef={addressState.suggestionsRef}
          onSuggestionClick={handleAddressSuggestionClick}
          onToggleExpanded={toggleAddressExpanded}
          renderSuggestion={(suggestion) =>
            `${suggestion.street} ${suggestion.houseNumber}, ${suggestion.postalCode} ${suggestion.city}`
          }
          fontClass={font.medium}
          formatMessage={formatMessage}
          ariaLabel='Address suggestions'
        />
      </div>
      <Field
        control={form.control}
        config={fieldConfig.address2}
        error={form.formState.errors?.address2}
        isDisabled={form.formState.isSubmitting}
      />
    </div>
  )
}

export default AddressFields
